import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc, limit } from 'firebase/firestore';
import { db, auth } from './firebase';
import './EmailLogReport.css';

const CHECK_BOUNCES_URL = 'https://us-central1-babs-2ee3f.cloudfunctions.net/checkBouncedEmails';

function EmailLogReport({ onClose }) {
  const [emailLogs, setEmailLogs] = useState([]);
  const [bulkLogs, setBulkLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('individual'); // 'individual' or 'bulk'

  // Filters
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'success', 'failed', 'bounced'
  const [filterSchool, setFilterSchool] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Stats
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, bounced: 0 });

  // Bounce checking
  const [checkingBounces, setCheckingBounces] = useState(false);
  const [bounceCheckResult, setBounceCheckResult] = useState(null);

  useEffect(() => {
    loadEmailLogs();
    loadBulkLogs();
  }, []);

  // ============================================================
  // LOAD DATA
  // ============================================================

  const loadEmailLogs = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      const q = query(
        collection(db, 'emailLogs'),
        where('franchiseeRef', '==', franchiseeRef),
        where('emailType', '==', 'invoice'),
        orderBy('timestamp', 'desc'),
        limit(500)
      );

      const snapshot = await getDocs(q);
      const logs = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestampDate: d.data().timestamp?.toDate ? d.data().timestamp.toDate() : new Date(d.data().timestamp)
      }));

      setEmailLogs(logs);

      // Calculate stats including bounced
      const sent = logs.filter(l => l.success && !l.bounced).length;
      const bounced = logs.filter(l => l.bounced === true).length;
      const failed = logs.filter(l => !l.success && !l.bounced).length;
      setStats({ total: logs.length, sent, failed, bounced });

    } catch (error) {
      console.error('Error loading email logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBulkLogs = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      const q = query(
        collection(db, 'invoiceBulkEmailLogs'),
        where('franchiseeRef', '==', franchiseeRef),
        orderBy('sentAt', 'desc'),
        limit(100)
      );

      const snapshot = await getDocs(q);
      const logs = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        sentAtDate: d.data().sentAt?.toDate ? d.data().sentAt.toDate() : new Date(d.data().sentAt)
      }));

      setBulkLogs(logs);
    } catch (error) {
      console.error('Error loading bulk email logs:', error);
    }
  };

  // ============================================================
  // CHECK FOR BOUNCES
  // ============================================================

  const checkForBounces = async () => {
  setCheckingBounces(true);
  setBounceCheckResult(null);
  try {
    // Load SMTP config from the Franchisee's own document
    const user = auth.currentUser;
    const franchiseeDoc = await getDoc(doc(db, 'Franchisees', user.uid));

    if (!franchiseeDoc.exists()) {
      setBounceCheckResult({ type: 'error', message: 'Franchisee profile not found.' });
      setCheckingBounces(false);
      return;
    }

    const data = franchiseeDoc.data();
    const smtpConfig = {
      host: data.outgoingServer,
      port: data.port,
      secure: data.useSSL,
      username: data.username,
      password: data.password,
    };

    if (!smtpConfig.username || !smtpConfig.password) {
      setBounceCheckResult({ type: 'error', message: 'No email configuration found. Please set up your email settings first.' });
      setCheckingBounces(false);
      return;
    }

    const response = await fetch(CHECK_BOUNCES_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ smtpConfig, franchiseeId: user.uid })
    });

    const result = await response.json();
    const found = result.bouncesFound ?? 0;
    const updated = result.logsUpdated ?? 0;

    if (found > 0) {
      setBounceCheckResult({ type: 'warning', message: `Found ${found} bounce email(s) — ${updated} log(s) updated.` });
      await loadEmailLogs();
    } else {
      setBounceCheckResult({ type: 'success', message: 'No new bounce emails found — all clear! ✅' });
    }
  } catch (e) {
    setBounceCheckResult({ type: 'error', message: 'Error checking bounces: ' + e.message });
  } finally {
    setCheckingBounces(false);
  }
};

  // ============================================================
  // FILTERING
  // ============================================================

  const getFilteredLogs = () => {
    let filtered = [...emailLogs];

    // Status filter
    if (filterStatus === 'success') {
      filtered = filtered.filter(l => l.success && !l.bounced);
    } else if (filterStatus === 'failed') {
      filtered = filtered.filter(l => !l.success && !l.bounced);
    } else if (filterStatus === 'bounced') {
      filtered = filtered.filter(l => l.bounced === true);
    }

    // School filter
    if (filterSchool) {
      filtered = filtered.filter(l =>
        (l.schoolName || '').toLowerCase().includes(filterSchool.toLowerCase())
      );
    }

    // Date range
    if (filterDateFrom) {
      const fromDate = new Date(filterDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(l => l.timestampDate >= fromDate);
    }
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(l => l.timestampDate <= toDate);
    }

    return filtered;
  };

  // ============================================================
  // HELPERS
  // ============================================================

  const formatDate = (date) => {
    if (!date) return '—';
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterSchool('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  // ============================================================
  // RENDER - Individual Email Logs Tab
  // ============================================================

  const renderIndividualLogs = () => {
    const filteredLogs = getFilteredLogs();

    return (
      <div>
        {/* Check for Bounces Button */}
        <div className="bounce-check-bar">
          <div className="bounce-check-left">
            <span className="bounce-check-info">
              ↩ Bounces are emails your mail server accepted but could not deliver to the recipient's inbox.
            </span>
          </div>
          <button
            onClick={checkForBounces}
            disabled={checkingBounces}
            className="btn-check-bounces"
          >
            {checkingBounces ? '⏳ Checking...' : '↩ Check for Bounces'}
          </button>
        </div>

        {/* Bounce check result banner */}
        {bounceCheckResult && (
          <div className={`bounce-result-banner bounce-result-${bounceCheckResult.type}`}>
            {bounceCheckResult.message}
            <button onClick={() => setBounceCheckResult(null)} className="bounce-result-close">✕</button>
          </div>
        )}

        {/* Filters */}
        <div className="log-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
                <option value="all">All ({stats.total})</option>
                <option value="success">Sent ({stats.sent})</option>
                <option value="failed">Failed ({stats.failed})</option>
                <option value="bounced">↩ Bounced ({stats.bounced})</option>
              </select>
            </div>
            <div className="filter-group">
              <label>School</label>
              <input
                type="text"
                value={filterSchool}
                onChange={(e) => setFilterSchool(e.target.value)}
                placeholder="Search school..."
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label>From</label>
              <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="filter-input" />
            </div>
            <div className="filter-group">
              <label>To</label>
              <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="filter-input" />
            </div>
            <button onClick={clearFilters} className="btn-clear-filters">Clear</button>
          </div>
        </div>

        {/* Results count */}
        <div className="results-count">
          Showing {filteredLogs.length} of {emailLogs.length} emails
          {stats.bounced > 0 && (
            <span className="bounced-count-hint"> · ↩ {stats.bounced} bounced</span>
          )}
        </div>

        {/* Log table */}
        {filteredLogs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No email logs found</h3>
            <p>{emailLogs.length === 0 ? 'No emails have been sent yet.' : 'Try adjusting your filters.'}</p>
          </div>
        ) : (
          <div className="log-table-container">
            <table className="log-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Student</th>
                  <th>School</th>
                  <th>Recipient</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id} className={
                    log.bounced ? 'row-bounced' : log.success ? 'row-success' : 'row-failed'
                  }>
                    <td>
                      {log.bounced ? (
                        <span className="status-badge badge-bounced">↩ Bounced</span>
                      ) : (
                        <span className={`status-badge ${log.success ? 'badge-success' : 'badge-failed'}`}>
                          {log.success ? '✓ Sent' : '✗ Failed'}
                        </span>
                      )}
                    </td>
                    <td className="date-cell">{formatDate(log.timestampDate)}</td>
                    <td className="student-cell">{log.studentName || '—'}</td>
                    <td className="school-cell">{log.schoolName || '—'}</td>
                    <td className="email-cell">{log.recipient || '—'}</td>
                    <td className="details-cell">
                      {log.bounced ? (
                        <span className="detail-bounce-reason">
                          {log.bounceReason ? log.bounceReason.substring(0, 80) + '...' : 'Email bounced — update the parent\'s email address'}
                        </span>
                      ) : log.success ? (
                        <span className="detail-text">
                          {log.hasAttachment && '📎 '}
                          {log.messageId ? `ID: ${log.messageId.substring(0, 16)}...` : '—'}
                        </span>
                      ) : (
                        <span className="detail-error">{log.error || 'Unknown error'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Bounce tip */}
        {stats.bounced > 0 && (
          <div className="bounce-tip-box">
            <strong>↩ You have {stats.bounced} bounced email(s).</strong> This means the parent's email address is incorrect or their inbox is full.
            Go to the student's profile and update their email address, then resend the invoice.
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // RENDER - Bulk Email Logs Tab
  // ============================================================

  const renderBulkLogs = () => {
    return (
      <div>
        {bulkLogs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No bulk email logs found</h3>
            <p>No bulk school emails have been sent yet.</p>
          </div>
        ) : (
          <div className="bulk-logs-list">
            {bulkLogs.map((log) => (
              <div key={log.id} className="bulk-log-card">
                <div className="bulk-log-header">
                  <div className="bulk-school-name">🏫 {log.schoolName || 'Unknown School'}</div>
                  <div className="bulk-date">{formatDate(log.sentAtDate)}</div>
                </div>
                <div className="bulk-log-stats">
                  <div className="bulk-stat">
                    <span className="bulk-stat-number bulk-green">{log.emailsSent || 0}</span>
                    <span className="bulk-stat-label">Sent</span>
                  </div>
                  <div className="bulk-stat">
                    <span className="bulk-stat-number bulk-red">{log.emailsFailed || 0}</span>
                    <span className="bulk-stat-label">Failed</span>
                  </div>
                  <div className="bulk-stat">
                    <span className="bulk-stat-number bulk-orange">{log.noEmailCount || 0}</span>
                    <span className="bulk-stat-label">No Email</span>
                  </div>
                  <div className="bulk-stat">
                    <span className="bulk-stat-number bulk-blue">{log.studentCount || 0}</span>
                    <span className="bulk-stat-label">Total</span>
                  </div>
                </div>
                <div className="bulk-log-meta">
                  <span>Sent by: {log.sentBy || 'Unknown'}</span>
                  <span>Type: {log.statementType || 'current'}</span>
                  {log.invalidEmailCount > 0 && (
                    <span className="meta-warning">{log.invalidEmailCount} invalid email(s)</span>
                  )}
                </div>
                {log.results && log.results.length > 0 && (
                  <details className="bulk-results-details">
                    <summary>View individual results ({log.results.length})</summary>
                    <div className="bulk-results-list">
                      {log.results.map((r, i) => (
                        <div key={i} className={`bulk-result-item ${r.success ? 'result-ok' : 'result-fail'}`}>
                          <span className="result-icon">{r.success ? '✓' : '✗'}</span>
                          <span className="result-name">{r.studentName}</span>
                          <span className="result-email">{r.email}</span>
                          {!r.success && <span className="result-reason">{r.message}</span>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <div className="email-log-container">
      <div className="email-log-header">
        <div className="header-left">
          <h1>📧 Email Delivery Log</h1>
          <p className="subtitle">View history of all invoice/statement emails sent</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn-back">← Back</button>
        )}
      </div>

      {/* Summary Stats */}
      <div className="email-log-stats-row">
        <div className="log-stat-card stat-card-total">
          <span className="log-stat-number">{stats.total}</span>
          <span className="log-stat-label">Total Emails</span>
        </div>
        <div className="log-stat-card stat-card-sent">
          <span className="log-stat-number">{stats.sent}</span>
          <span className="log-stat-label">Sent Successfully</span>
        </div>
        <div className="log-stat-card stat-card-failed">
          <span className="log-stat-number">{stats.failed}</span>
          <span className="log-stat-label">Failed</span>
        </div>
        <div className="log-stat-card stat-card-bounced">
          <span className="log-stat-number">{stats.bounced}</span>
          <span className="log-stat-label">↩ Bounced</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="email-log-tabs">
        <button
          className={`tab-btn ${activeTab === 'individual' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('individual')}
        >
          📋 Individual Emails ({emailLogs.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'bulk' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('bulk')}
        >
          🏫 Bulk School Sends ({bulkLogs.length})
        </button>
        <button onClick={loadEmailLogs} className="tab-btn tab-refresh" disabled={loading}>
          {loading ? '⏳' : '🔄'} Refresh
        </button>
      </div>

      {/* Tab Content */}
      <div className="email-log-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading email logs...</p>
          </div>
        ) : (
          activeTab === 'individual' ? renderIndividualLogs() : renderBulkLogs()
        )}
      </div>

      {/* Info footer */}
      <div className="email-log-footer-info">
        <strong>ℹ️ About email delivery:</strong> "Sent" means the email was accepted by the mail server.
        Use the <strong>Check for Bounces</strong> button to detect emails that were accepted but could not be delivered.
        Failed emails show the specific error reason.
      </div>
    </div>
  );
}

export default EmailLogReport;